import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getLandingData, createInquiry, register } from '../api';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import KittenTicker from '../components/KittenTicker';
import Section, { SectionTitle } from '../components/Section';
import { toast } from 'sonner';
import {
  Heart, Shield, Sparkles, Users, Star, X, ChevronLeft, ChevronRight,
  ArrowRight, Send, Cat, Snowflake, BookOpen, UserPlus, Eye, EyeOff,
  Calendar, Dna, Droplets, Palette, MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = ({ user, onLogout, onLogin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [formStep, setFormStep] = useState(1);
  const [isGeneralOnly, setIsGeneralOnly] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [registerForm, setRegisterForm] = useState({ password: '', password2: '' });
  const [showRegPw, setShowRegPw] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [animalPopup, setAnimalPopup] = useState(null);
  const [animalFullImg, setAnimalFullImg] = useState(null);
  const [uspPopup, setUspPopup] = useState(null);
  const [contactMode, setContactMode] = useState('form'); // 'form' | 'phone'
  const [reviewPopup, setReviewPopup] = useState(null);
  const [kittenPopup, setKittenPopup] = useState(false);
  const [kittenForm, setKittenForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [kittenSubmitting, setKittenSubmitting] = useState(false);

  // Listen for kitten popup event from Navigation
  useEffect(() => {
    const handler = () => setKittenPopup(true);
    window.addEventListener('open-kitten-popup', handler);
    return () => window.removeEventListener('open-kitten-popup', handler);
  }, []);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', plz: '', message: '',
    inquiry_type: 'interest', housing: '', birman_experience: false,
    wanted_timeframe: '', has_other_pets: false, preferred_color: '',
    preferred_gender: '', how_found: '', intro_text: ''
  });

  useEffect(() => {
    getLandingData()
      .then((res) => setData(res.data))
      .catch((err) => console.error('Landing data error:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFormSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Bitte Name, E-Mail und Telefonnummer ausfüllen');
      return;
    }
    setSubmitting(true);
    try {
      await createInquiry(formData);
      toast.success('Vielen Dank! Ihre Anfrage wurde gesendet.');
      if (!user) {
        setShowRegisterPrompt(true);
      } else {
        setFormData({ name: '', phone: '', email: '', plz: '', message: '', inquiry_type: 'general', housing: '', birman_experience: false, wanted_timeframe: '', has_other_pets: false, preferred_color: '', preferred_gender: '', how_found: '', intro_text: '' });
        setFormStep(1);
      }
    } catch (err) {
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally { setSubmitting(false); }
  };

  const handleRegister = async () => {
    if (!registerForm.password || registerForm.password.length < 6) { toast.error('Passwort muss mindestens 6 Zeichen haben'); return; }
    if (registerForm.password !== registerForm.password2) { toast.error('Passwörter stimmen nicht überein'); return; }
    setRegistering(true);
    try {
      const res = await register({ email: formData.email, password: registerForm.password, full_name: formData.name, phone: formData.phone || '' });
      if (onLogin) onLogin(res.data.user, res.data.token);
      toast.success('Konto erstellt! Sie haben jetzt Zugang zum Wissensportal.');
      setShowRegisterPrompt(false);
      navigate('/dashboard');
    } catch (err) { toast.error(err.response?.data?.detail || 'Registrierung fehlgeschlagen'); }
    finally { setRegistering(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EEF3F8]">
        <Snowflake size={48} className="text-[#7A9BB5] mx-auto mb-4 animate-spin" style={{ animationDuration: '3s' }} />
      </div>
    );
  }

  const { settings = {}, cms = {}, queens = [], studs = [], youngsters = [], castrated = [], retired = [], reviews = [], google_reviews = [], gallery = [], announcements = [], affiliates = [] } = data || {};
  const allReviews = [...(google_reviews || []).map(r => ({...r, source: 'google'})), ...reviews];
  const uspIcons = [Heart, Shield, Sparkles, Users];

  // Animal Card Component (inline with popup support)
  const AnimalCardMini = ({ animal, isPlaceholder, placeholderText }) => {
    if (isPlaceholder) {
      return (
        <div className="placeholder-card animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-[#C5D0DA]/30 flex items-center justify-center mb-3">
            <Cat size={28} className="text-[#7A9BB5]" />
          </div>
          <p className="text-sm text-center px-4 text-[#7A9BB5]">{placeholderText}</p>
        </div>
      );
    }
    const mainPhoto = animal.photos?.length > 0 ? `${BACKEND_URL}${animal.photos[0]}` : null;
    // Get section-specific text sizes
    const section = animal.type === 'queen' ? 'queens' : 'studs';
    const nameColor = settings[`section_heading_color_${section}`] || '#2E3A46';
    const detailColor = settings[`section_body_color_${section}`] || '#7A9BB5';
    const nameSize = settings[`section_heading_size_${section}`] ? `${Math.max(parseInt(settings[`section_heading_size_${section}`]) / 2.5, 16)}px` : '1.125rem';
    return (
      <button onClick={() => setAnimalPopup(animal)} className="card-frost overflow-hidden group text-left w-full">
        <div className="aspect-[4/3] overflow-hidden bg-[#D8E8F0]">
          {mainPhoto ? (
            <img src={mainPhoto} alt={animal.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Cat size={40} className="text-[#C5D0DA]" /></div>
          )}
        </div>
        <div className="p-4 text-center">
          <h3 className="font-heading" style={{ color: nameColor, fontSize: nameSize }}>
            {animal.country_code && <span className="font-mono px-1.5 py-0.5 rounded mr-1.5 align-middle" style={{ fontSize: '0.7em', backgroundColor: '#D8E8F0', color: detailColor }}>({animal.country_code})</span>}
            {animal.name}
          </h3>
          {animal.cattery_name && <p className="italic mt-0.5" style={{ color: detailColor, fontSize: `${Math.max(parseFloat(nameSize) * 0.65, 11)}px` }}>{animal.cattery_name}</p>}
          {animal.base_color && <p className="font-medium mt-1" style={{ color: detailColor, fontSize: `${Math.max(parseFloat(nameSize) * 0.75, 12)}px` }}>{animal.base_color}</p>}
        </div>
      </button>
    );
  };

  // Render animal grid with centering for 2 items
  const AnimalGrid = ({ animals, placeholderCount, placeholderText }) => {
    const items = animals.length > 0 ? animals : Array.from({ length: placeholderCount }, (_, i) => ({ _placeholder: true, _key: i }));
    const count = items.length;
    return (
      <div className="flex flex-wrap justify-center gap-4 md:gap-6">
        {items.map((item, i) => (
          <div key={item._placeholder ? i : item.id} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-[380px]">
            {item._placeholder
              ? <AnimalCardMini isPlaceholder placeholderText={placeholderText} />
              : <AnimalCardMini animal={item} />
            }
          </div>
        ))}
      </div>
    );
  };

  const tickerProps = { active: settings.kitten_mode_active, text: settings.ticker_text, bgColor: settings.ticker_bg_color, textColor: settings.ticker_text_color, fontSize: settings.ticker_font_size, fontFamily: settings.ticker_font, height: settings.ticker_height };
  const tickerPos = settings.ticker_position || 'above_banner';
  const tickerBetween = (settings.ticker_between_sections || '').split(',').filter(Boolean);
  const TickerAt = ({ pos }) => tickerPos === pos ? <KittenTicker {...tickerProps} /> : null;
  const TickerBetween = ({ section }) => tickerBetween.includes(section) ? <KittenTicker {...tickerProps} /> : null;

  return (
    <div id="home" className="min-h-screen bg-[#EEF3F8]">
      {/* TICKER above banner */}
      <TickerAt pos="above_banner" />

      {/* BANNER */}
      <div className="relative w-full" data-testid="landing-hero">
        <img src={settings.banner_landing_url ? `${BACKEND_URL}${settings.banner_landing_url}` : `${window.location.origin}/banner.jpg`} alt="Birma von den Schneeblumen" className="w-full h-auto min-h-[250px] max-h-[75vh] object-cover" />
      </div>

      {/* TICKER below banner */}
      <TickerAt pos="below_banner" />

      {/* NAVIGATION (sticky) */}
      <Navigation user={user} onLogout={onLogout} settings={settings} isLanding={true} />

      {/* TICKER below nav */}
      <TickerAt pos="below_nav" />

      {/* WILLKOMMEN */}
      <Section id="welcome" variant="ice" bgColor={settings.section_bg_welcome} bgImage={settings.section_bg_image_welcome} bgOpacity={settings.section_bg_opacity_welcome} headingColor={settings.section_heading_color_welcome} bodyColor={settings.section_body_color_welcome} headingSize={settings.section_heading_size_welcome}>
        <div className="max-w-3xl mx-auto text-center animate-fade-in" data-testid="welcome-section">
          <div className="w-12 h-0.5 bg-[#C9A96E] mx-auto mb-8"></div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-6"
            style={{ color: settings.section_heading_color_welcome || '#2E3A46', fontSize: settings.section_heading_size_welcome ? `${settings.section_heading_size_welcome}px` : undefined }}>
            {cms.welcome_text?.title || 'Familienzucht für Heilige Birma'}
          </h2>
          <p className="text-base md:text-lg leading-relaxed"
            style={{ color: settings.section_body_color_welcome || '#354551' }}>
            {cms.welcome_text?.body || 'Herzlich willkommen bei den Schneeblumen.'}
          </p>
          <div className="w-12 h-0.5 bg-[#C9A96E] mx-auto mt-8"></div>
        </div>
      </Section>

      {/* ZUCHTKATZEN */}
      <Section id="queens" variant="even" bgColor={settings.section_bg_queens} bgImage={settings.section_bg_image_queens} bgOpacity={settings.section_bg_opacity_queens} headingColor={settings.section_heading_color_queens} bodyColor={settings.section_body_color_queens} headingSize={settings.section_heading_size_queens}>
        <SectionTitle subtitle="Unsere wundervollen Zuchtkatzen">Unsere Katzen</SectionTitle>
        <AnimalGrid animals={queens} placeholderCount={3} placeholderText="Unsere Zuchtkatze wird bald vorgestellt" />
      </Section>
      <TickerBetween section="queens" />

      {/* KATER */}
      <Section id="studs" variant="odd" bgColor={settings.section_bg_studs} bgImage={settings.section_bg_image_studs} bgOpacity={settings.section_bg_opacity_studs} headingColor={settings.section_heading_color_studs} bodyColor={settings.section_body_color_studs} headingSize={settings.section_heading_size_studs}>
        <SectionTitle subtitle="Unsere stattlichen Deckkater">Unsere Kater</SectionTitle>
        <AnimalGrid animals={studs} placeholderCount={2} placeholderText="Unser Kater wird bald vorgestellt" />
      </Section>
      <TickerBetween section="studs" />

      {/* JUNGTIERE */}
      {youngsters.length > 0 && (
        <Section id="youngsters" variant="even">
          <SectionTitle subtitle="Unsere jungen Schneeblumen">Unsere Jungtiere</SectionTitle>
          <AnimalGrid animals={youngsters} placeholderCount={0} placeholderText="" />
        </Section>
      )}

      {/* KASTRATEN */}
      {castrated.length > 0 && (
        <Section id="castrated" variant="odd">
          <SectionTitle subtitle="Suchen ein liebevolles neues Zuhause">Kastraten & ein neues Zuhause</SectionTitle>
          <AnimalGrid animals={castrated} placeholderCount={0} placeholderText="" />
        </Section>
      )}

      {/* EHEMALIGE */}
      {retired.length > 0 && (
        <Section id="retired" variant="even">
          <SectionTitle subtitle="In liebevoller Erinnerung">Unsere ehemaligen Schneeblumen</SectionTitle>
          <AnimalGrid animals={retired} placeholderCount={0} placeholderText="" />
        </Section>
      )}

      {/* USPs - Warum Schneeblumen */}
      <Section id="usps" variant="dark-frame" bgColor={settings.section_bg_usps} bgImage={settings.section_bg_image_usps} bgOpacity={settings.section_bg_opacity_usps} headingColor={settings.section_heading_color_usps} bodyColor={settings.section_body_color_usps} headingSize={settings.section_heading_size_usps}>
        <SectionTitle light subtitle="Was uns als Züchter auszeichnet">Warum Schneeblumen?</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="usp-section">
          {[1, 2, 3, 4].map((i) => {
            const usp = cms[`usp_${i}`];
            const Icon = uspIcons[i - 1] || Heart;
            return (
              <button key={i} onClick={() => setUspPopup(usp)} className="bg-[#2E3A46] border border-[#C5D0DA]/20 rounded-2xl p-6 text-center hover:border-[#7A9BB5]/40 transition-colors cursor-pointer w-full">
                <div className="w-14 h-14 rounded-2xl bg-[#7A9BB5]/20 flex items-center justify-center mx-auto mb-4">
                  <Icon size={24} className="text-[#7A9BB5]" />
                </div>
                <h3 className="font-heading text-lg text-white mb-2">{usp?.title || `USP ${i}`}</h3>
                <p className="text-sm text-[#C5D0DA] leading-relaxed line-clamp-2">{usp?.body || ''}</p>
                <span className="text-xs text-[#7A9BB5] mt-3 inline-block">Mehr erfahren →</span>
              </button>
            );
          })}
        </div>
      </Section>
      <TickerBetween section="usps" />

      {/* KUNDENSTIMMEN */}
      {allReviews.length > 0 && (
        <Section id="reviews" variant="ice">
          <SectionTitle subtitle="Was unsere Schneeblumen-Familien sagen">Kundenstimmen</SectionTitle>
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 ${allReviews.length <= 2 ? 'max-w-3xl mx-auto' : ''}`} data-testid="reviews-section">
            {allReviews.map((review, idx) => (
              <button key={review.id || `gr-${idx}`} onClick={() => setReviewPopup(review)} className="card-frost p-6 text-left w-full hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={16} className={s <= review.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-[#C5D0DA]'} />
                    ))}
                  </div>
                  {review.source === 'google' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#4285F4]/10 text-[#4285F4] font-medium">Google</span>
                  )}
                </div>
                <p className="text-sm text-[#354551] leading-relaxed mb-4 italic line-clamp-3">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-[#2E3A46]">{review.name}</p>
                  {review.photos?.length > 0 && <span className="text-xs text-[#7A9BB5]">{review.photos.length} Foto(s)</span>}
                </div>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* GALERIE */}
      <Section id="gallery" variant="even" bgColor={settings.section_bg_gallery} bgImage={settings.section_bg_image_gallery} bgOpacity={settings.section_bg_opacity_gallery} headingColor={settings.section_heading_color_gallery} bodyColor={settings.section_body_color_gallery} headingSize={settings.section_heading_size_gallery}>
        <SectionTitle subtitle="Einblicke in die Welt der Schneeblumen">Galerie</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="gallery-section">
          {gallery.length > 0 ? gallery.map((item, index) => (
            <div key={item.id} className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative" onClick={() => setLightbox({ open: true, index })} data-testid="gallery-image">
              {item.type === 'video' || item.url?.match(/\.(mp4|mov|webm|avi)$/i) ? (
                <>
                  <video src={`${BACKEND_URL}${item.url}`} className="w-full h-full object-cover" muted />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center"><span className="ml-1 text-[#2E3A46] text-lg">&#9654;</span></div>
                  </div>
                </>
              ) : (
                <img src={`${BACKEND_URL}${item.url}`} alt={item.caption || 'Galerie'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              )}
            </div>
          )) : [1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="aspect-square rounded-xl bg-white border-2 border-dashed border-[#C5D0DA] flex items-center justify-center">
              <div className="text-center"><Snowflake size={24} className="text-[#C5D0DA] mx-auto mb-1" /><p className="text-xs text-[#C5D0DA]">Bald hier</p></div>
            </div>
          ))}
        </div>
      </Section>
      <TickerBetween section="gallery" />

      {/* Lightbox */}
      {lightbox.open && gallery.length > 0 && (
        <div className="fixed inset-0 bg-[#2E3A46]/92 z-[60] flex items-center justify-center" onClick={() => setLightbox({ open: false, index: 0 })}>
          <button className="absolute top-4 right-4 text-white z-10 p-2 hover:bg-white/10 rounded-full" onClick={() => setLightbox({ open: false, index: 0 })}><X size={28} /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white z-10 p-3 hover:bg-white/10 rounded-full" onClick={(e) => { e.stopPropagation(); setLightbox(p => ({ ...p, index: (p.index - 1 + gallery.length) % gallery.length })); }}><ChevronLeft size={28} /></button>
          {(gallery[lightbox.index]?.type === 'video' || gallery[lightbox.index]?.url?.match(/\.(mp4|mov|webm|avi)$/i)) ? (
            <video src={`${BACKEND_URL}${gallery[lightbox.index]?.url}`} controls autoPlay className="max-h-[85vh] max-w-[90vw] rounded-lg" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={`${BACKEND_URL}${gallery[lightbox.index]?.url}`} alt="Galerie" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          )}
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white z-10 p-3 hover:bg-white/10 rounded-full" onClick={(e) => { e.stopPropagation(); setLightbox(p => ({ ...p, index: (p.index + 1) % gallery.length })); }}><ChevronRight size={28} /></button>
          <div className="absolute bottom-4 text-white text-sm">{lightbox.index + 1} / {gallery.length}</div>
        </div>
      )}

      {/* NEUIGKEITEN */}
      {announcements.length > 0 && (
        <Section id="news" variant="ice">
          <SectionTitle subtitle="Aktuelle Informationen von den Schneeblumen">Neuigkeiten</SectionTitle>
          <div className="space-y-6 max-w-4xl mx-auto">
            {announcements.map((ann) => (
              <div key={ann.id} className="card-frost overflow-hidden">
                {/* Images */}
                {ann.images?.length > 0 && (
                  <div className={ann.images.length === 1 ? '' : 'grid grid-cols-2 gap-0.5'}>
                    {ann.images.map((url, i) => (
                      <img key={i} src={`${BACKEND_URL}${url}`} alt={ann.title} className={`w-full object-cover ${ann.images.length === 1 ? 'h-64 md:h-80' : 'h-40 md:h-52'}`} loading="lazy" />
                    ))}
                  </div>
                )}
                {/* Videos */}
                {ann.videos?.length > 0 && (
                  <div className="bg-black">
                    {ann.videos.map((url, i) => (
                      <video key={i} src={`${BACKEND_URL}${url}`} controls className="w-full max-h-96" />
                    ))}
                  </div>
                )}
                <div className="p-5 md:p-6">
                  <p className="text-xs text-[#7A9BB5] mb-2">{new Date(ann.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <h3 className="font-heading text-xl text-[#2E3A46] mb-2">{ann.title}</h3>
                  {ann.body && <p className="text-[#354551] leading-relaxed whitespace-pre-line">{ann.body}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ÜBER UNS */}
      <Section id="about" variant="odd" bgColor={settings.section_bg_about} bgImage={settings.section_bg_image_about} bgOpacity={settings.section_bg_opacity_about} headingColor={settings.section_heading_color_about} bodyColor={settings.section_body_color_about} headingSize={settings.section_heading_size_about}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-testid="about-section">
          <div>
            <div className="w-10 h-0.5 bg-[#C9A96E] mb-6"></div>
            <h2 className="font-heading text-3xl sm:text-4xl mb-6"
              style={{ color: settings.section_heading_color_about || '#2E3A46' }}>
              {cms.about_text?.title || 'Über uns'}
            </h2>
            <p className="leading-relaxed"
              style={{ color: settings.section_body_color_about || '#354551' }}>
              {cms.about_text?.body || ''}
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-[#7A9BB5]">
              <Shield size={16} /><span>Mitglied im 1. Felidae Cat Club e.V.</span>
            </div>
          </div>
          <div className="flex justify-center">
            {cms.about_text?.image_url ? (
              <img src={`${BACKEND_URL}${cms.about_text.image_url}`} alt="Dorothea & Andreas" className="w-full max-w-md aspect-[4/3] rounded-2xl object-cover shadow-lg" />
            ) : (
              <div className="w-full max-w-md aspect-[4/3] rounded-2xl bg-white border-2 border-dashed border-[#C5D0DA] flex items-center justify-center">
                <div className="text-center"><Users size={48} className="text-[#C5D0DA] mx-auto mb-3" /><p className="text-sm text-[#C5D0DA]">Foto von Dorothea & Andreas<br />folgt in Kürze</p></div>
              </div>
            )}
          </div>
        </div>
      </Section>
      <TickerBetween section="about" />

      {/* EMPFEHLUNGEN / AFFILIATES */}
      {affiliates.length > 0 && (
        <Section id="empfehlungen" variant="odd">
          <SectionTitle subtitle="Produkte die wir aus eigener Erfahrung empfehlen">Unsere Empfehlungen</SectionTitle>
          <div className="flex flex-wrap justify-center gap-4">
            {affiliates.slice(0, 6).map(aff => {
              const catLabels = { wasserfilter: 'Wasserfilter', nassfutter: 'Nassfutter', trockenfutter: 'Trockenfutter', kratzbaum: 'Kratzbäume', spielzeug: 'Spielzeug', pflegeprodukt: 'Pflegeprodukte', transportbox: 'Transportboxen', streu: 'Streu', sicherheitsnetz: 'Sicherheitsnetze' };
              return (
                <a key={aff.id} href={aff.affiliate_url || '#'} target="_blank" rel="noopener noreferrer"
                  className="card-frost p-5 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] max-w-[380px] hover:shadow-lg transition-shadow relative group">
                  <span className="absolute top-3 right-3 text-[10px] px-1.5 py-0.5 rounded bg-[#2E3A46]/70 text-white">Werbung</span>
                  <h4 className="font-medium text-[#2E3A46] mb-1 group-hover:text-[#7A9BB5] pr-14">{aff.name}</h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#D8E8F0] text-[#7A9BB5]">{catLabels[aff.category] || aff.category}</span>
                  {aff.description && <p className="text-sm text-[#354551] mt-2 line-clamp-2">{aff.description}</p>}
                </a>
              );
            })}
          </div>
          {affiliates.length > 6 && (
            <div className="text-center mt-6">
              <a href="/ratgeber" className="btn-outline inline-flex items-center gap-2">Alle Empfehlungen ansehen <ChevronRight size={14} /></a>
            </div>
          )}
        </Section>
      )}

      {/* ANFRAGE & KONTAKT */}
      <Section id="contact" variant="dark-frame" bgColor={settings.section_bg_contact} bgImage={settings.section_bg_image_contact} bgOpacity={settings.section_bg_opacity_contact} headingColor={settings.section_heading_color_contact} bodyColor={settings.section_body_color_contact} headingSize={settings.section_heading_size_contact}>
        <SectionTitle light subtitle="Wir freuen uns auf Ihre Nachricht">Anfrage & Kontakt</SectionTitle>
        <div className="max-w-2xl mx-auto" data-testid="contact-section">
          {/* Three tabs */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <button onClick={() => { setContactMode('form'); setIsGeneralOnly(false); setFormStep(1); }}
              className={`px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${contactMode === 'form' && !isGeneralOnly ? 'bg-[#7A9BB5] text-white' : 'bg-[#2E3A46] text-[#C5D0DA] border border-[#C5D0DA]/30'}`}>
              Kitten-Interesse
            </button>
            <button onClick={() => { setContactMode('form'); setIsGeneralOnly(true); setFormStep(1); }}
              className={`px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${contactMode === 'form' && isGeneralOnly ? 'bg-[#7A9BB5] text-white' : 'bg-[#2E3A46] text-[#C5D0DA] border border-[#C5D0DA]/30'}`}>
              Allgemeine Frage
            </button>
            <button onClick={() => setContactMode('phone')}
              className={`px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center gap-1.5 ${contactMode === 'phone' ? 'bg-[#C9A96E] text-white' : 'bg-[#2E3A46] text-[#C5D0DA] border border-[#C5D0DA]/30'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              Direkt anrufen
            </button>
          </div>

          {/* Phone / Direct Contact Tab */}
          {contactMode === 'phone' ? (
            <div className="text-center animate-fade-in py-6">
              <div className="w-20 h-20 rounded-full bg-[#C9A96E]/20 flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              </div>
              <h3 className="font-heading text-2xl text-[#EEF3F8] mb-3">Rufen Sie uns direkt an</h3>
              <p className="text-[#C5D0DA] mb-6 text-sm">
                {cms.breeder_name?.body || 'Dorothea Schulz'} freut sich auf Ihren Anruf.
              </p>
              <a href={`tel:${(cms.breeder_phone?.body || '+4917631134989').replace(/[^+\d]/g, '')}`}
                className="inline-flex items-center gap-3 bg-[#C9A96E] hover:bg-[#b8943f] text-white px-8 py-4 rounded-2xl text-xl font-heading transition-colors shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                {cms.breeder_phone?.body || '+49(0)176 311 34 989'}
              </a>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="p-4 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/20">
                  <p className="text-[#7A9BB5] text-xs mb-1">Adresse</p>
                  <p className="text-[#EEF3F8]">{cms.breeder_name?.body || 'Dorothea Schulz'}</p>
                  <p className="text-[#C5D0DA]">{cms.breeder_address?.body || 'Royberg 2c, 21271 Hanstedt'}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/20">
                  <p className="text-[#7A9BB5] text-xs mb-1">E-Mail</p>
                  <a href={`mailto:${cms.breeder_email?.body || 'info@birma-schneeblumen.de'}`} className="text-[#EEF3F8] hover:text-[#7A9BB5] transition-colors">
                    {cms.breeder_email?.body || 'info@birma-schneeblumen.de'}
                  </a>
                  <p className="text-[#C5D0DA] mt-2 text-xs">{cms.breeder_club?.body || 'Mitglied im 1. Felidae Cat Club e.V.'}</p>
                </div>
              </div>
              {/* WhatsApp */}
              {settings.whatsapp_number && (
                <a href={`https://wa.me/${settings.whatsapp_number.replace(/[^+\d]/g, '')}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 bg-[#25D366] hover:bg-[#1da851] text-white px-6 py-3 rounded-xl text-sm font-medium transition-colors">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.624-1.467A11.932 11.932 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-2.168 0-4.19-.588-5.932-1.607l-.425-.253-2.742.87.888-2.654-.278-.441A9.776 9.776 0 012.182 12c0-5.418 4.4-9.818 9.818-9.818S21.818 6.582 21.818 12 17.418 21.818 12 21.818z"/></svg>
                  WhatsApp schreiben
                </a>
              )}
            </div>
          ) : (
          /* Form tabs */
          <div className="space-y-4">
            {(formStep === 1 || isGeneralOnly) && (
              <div className="space-y-4 animate-fade-in" data-testid="inquiry-form-step-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Name *</label><input type="text" name="name" value={formData.name} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none" placeholder="Ihr vollständiger Name" /></div>
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">E-Mail *</label><input type="email" name="email" value={formData.email} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none" placeholder="ihre@email.de" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Telefon *</label><input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none" placeholder="+49..." /></div>
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">PLZ</label><input type="text" name="plz" value={formData.plz} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none" placeholder="Postleitzahl" /></div>
                </div>
                {isGeneralOnly && (<div><label className="block text-sm text-[#C5D0DA] mb-1.5">Ihre Nachricht</label><textarea name="message" value={formData.message} onChange={handleFormChange} rows={4} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none resize-none" placeholder="Ihre Frage oder Nachricht..." /></div>)}
              </div>
            )}
            {formStep === 2 && !isGeneralOnly && (
              <div className="space-y-4 animate-fade-in" data-testid="inquiry-form-step-2">
                <p className="text-sm text-[#C9A96E] font-medium">Schritt 2 von 2 – Erzählen Sie uns mehr über sich</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Wohnsituation</label><select name="housing" value={formData.housing} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white focus:border-[#7A9BB5] focus:outline-none"><option value="">Bitte wählen...</option><option value="wohnung">Wohnung</option><option value="haus_garten">Haus mit Garten</option><option value="haus_ohne">Haus ohne Garten</option><option value="gehege">Mit Außengehege</option></select></div>
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Farbwunsch</label><select name="preferred_color" value={formData.preferred_color} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white focus:border-[#7A9BB5] focus:outline-none"><option value="">Keine Präferenz</option><option value="chocolate">Chocolate Point</option><option value="lilac">Lilac Point</option><option value="seal">Seal Point</option><option value="blue">Blue Point</option><option value="chocolate_tabby">Chocolate Tabby Point</option><option value="lilac_tabby">Lilac Tabby Point</option><option value="seal_tabby">Seal Tabby Point</option><option value="blue_tabby">Blue Tabby Point</option><option value="egal">Farbe egal</option></select></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Zeitraum</label><input type="text" name="wanted_timeframe" value={formData.wanted_timeframe} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none" placeholder="z.B. Frühjahr 2026" /></div>
                  <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Wie gefunden?</label><select name="how_found" value={formData.how_found} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white focus:border-[#7A9BB5] focus:outline-none"><option value="">Bitte wählen...</option><option value="google">Google</option><option value="facebook">Facebook</option><option value="instagram">Instagram</option><option value="empfehlung">Empfehlung</option></select></div>
                </div>
                <div><label className="block text-sm text-[#C5D0DA] mb-1.5">Persönliches Anschreiben</label><textarea name="intro_text" value={formData.intro_text} onChange={handleFormChange} rows={3} className="w-full px-4 py-3 rounded-xl bg-[#2E3A46] border border-[#C5D0DA]/30 text-white placeholder-[#7A9BB5] focus:border-[#7A9BB5] focus:outline-none resize-none" placeholder="Erzählen Sie uns von sich..." /></div>
              </div>
            )}
            <div className="flex items-start gap-2 text-xs text-[#C5D0DA]"><input type="checkbox" id="gdpr-check" className="mt-1 w-3.5 h-3.5 accent-[#7A9BB5]" /><span>Ich habe die <Link to="/datenschutz" className="underline hover:text-white">Datenschutzerklärung</Link> gelesen und stimme zu.</span></div>
            <div className="flex gap-3 pt-2">
              {formStep === 2 && !isGeneralOnly && (<button type="button" onClick={() => setFormStep(1)} className="px-6 py-3 rounded-xl bg-[#2E3A46] text-[#C5D0DA] border border-[#C5D0DA]/30 text-sm">Zurück</button>)}
              {formStep === 1 && !isGeneralOnly ? (
                <button type="button" onClick={() => { if (!formData.name || !formData.email || !formData.phone) { toast.error('Bitte Name, E-Mail und Telefon ausfüllen'); return; } setFormStep(2); }} className="flex-1 btn-primary flex items-center justify-center gap-2">Weiter <ArrowRight size={16} /></button>
              ) : (
                <button type="button" onClick={(e) => { const gdpr = document.getElementById('gdpr-check'); if (!gdpr?.checked) { toast.error('Bitte Datenschutzerklärung akzeptieren'); return; } handleFormSubmit(e); }} disabled={submitting} className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-60" data-testid="inquiry-form-submit-button">
                  {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><Send size={16} />{isGeneralOnly ? 'Nachricht senden' : 'Anfrage absenden'}</>}
                </button>
              )}
            </div>
          </div>
          )}
        </div>
      </Section>

      {/* ANIMAL POPUP */}
      {animalPopup && (
        <div className="fixed inset-0 bg-[#2E3A46]/80 z-[60] flex items-start md:items-center justify-center p-2 md:p-4 pt-8 md:pt-4 overflow-y-auto" onClick={() => setAnimalPopup(null)}>
          <div className="bg-white rounded-2xl w-[calc(100%-16px)] md:w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in my-2 md:my-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <button className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-[#2E3A46]/60 flex items-center justify-center text-white hover:bg-[#2E3A46]" onClick={() => setAnimalPopup(null)}><X size={18} /></button>
              {/* Main photo - clickable for fullscreen */}
              {animalPopup.photos?.length > 0 && (
                <img src={`${BACKEND_URL}${animalPopup.photos[0]}`} alt={animalPopup.name}
                  className="w-full h-64 sm:h-80 object-cover rounded-t-2xl cursor-pointer hover:opacity-95"
                  onClick={() => setAnimalFullImg(0)} />
              )}
              {/* Thumbnail row */}
              {animalPopup.photos?.length > 1 && (
                <div className="flex gap-1.5 p-2 bg-white overflow-x-auto">
                  {animalPopup.photos.map((p, i) => (
                    <img key={i} src={`${BACKEND_URL}${p}`} alt=""
                      className={`w-16 h-16 rounded-lg object-cover cursor-pointer shrink-0 hover:opacity-80 ${i === 0 ? 'ring-2 ring-[#7A9BB5]' : ''}`}
                      onClick={() => setAnimalFullImg(i)} />
                  ))}
                </div>
              )}
            </div>
            <div className="p-6">
              {animalPopup.prefix && <p className="text-xs text-[#7A9BB5] uppercase tracking-wider mb-1">{animalPopup.prefix}</p>}
              <h2 className="font-heading text-2xl text-[#2E3A46] mb-1">
                {animalPopup.country_code && <span className="text-sm font-mono bg-[#D8E8F0] text-[#7A9BB5] px-2 py-0.5 rounded mr-2 align-middle">({animalPopup.country_code})</span>}
                {animalPopup.name}
              </h2>
              {animalPopup.cattery_name && <p className="text-sm text-[#7A9BB5] italic mb-2">{animalPopup.cattery_name}</p>}
              {animalPopup.base_color && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#D8E8F0] text-[#2E3A46] text-sm mb-4"><Palette size={14} className="text-[#7A9BB5]" />{animalPopup.base_color}</span>}
              {animalPopup.character_text && (<div className="mt-4"><h4 className="font-heading text-base text-[#2E3A46] mb-1">Wesen & Charakter</h4><p className="text-sm text-[#354551] leading-relaxed">{animalPopup.character_text}</p></div>)}
              {animalPopup.bio && (<div className="mt-3"><h4 className="font-heading text-base text-[#2E3A46] mb-1">Biographie</h4><p className="text-sm text-[#354551] leading-relaxed">{animalPopup.bio}</p></div>)}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {animalPopup.born_at && (<div className="p-3 rounded-xl bg-[#EEF3F8]"><p className="text-xs text-[#7A9BB5] flex items-center gap-1"><Calendar size={12} /> Geburtsdatum</p><p className="text-sm font-medium text-[#2E3A46] mt-0.5">{animalPopup.born_at}</p></div>)}
                {animalPopup.blood_group && (<div className="p-3 rounded-xl bg-[#EEF3F8]"><p className="text-xs text-[#7A9BB5] flex items-center gap-1"><Droplets size={12} /> Blutgruppe</p><p className="text-sm font-medium text-[#2E3A46] mt-0.5">{animalPopup.blood_group}</p></div>)}
                {animalPopup.dilution_gene && (<div className="p-3 rounded-xl bg-[#EEF3F8]"><p className="text-xs text-[#7A9BB5] flex items-center gap-1"><Dna size={12} /> Verdünnung</p><p className="text-sm font-medium text-[#2E3A46] mt-0.5">{animalPopup.dilution_gene}</p></div>)}
                {animalPopup.felidae_number && (<div className="p-3 rounded-xl bg-[#EEF3F8]"><p className="text-xs text-[#7A9BB5] flex items-center gap-1"><Heart size={12} /> Felidae Nr.</p><p className="text-sm font-medium text-[#2E3A46] mt-0.5">{animalPopup.felidae_number}</p></div>)}
                {animalPopup.mother_id && (<div className="p-3 rounded-xl bg-[#EEF3F8]"><p className="text-xs text-[#7A9BB5]">Mutter</p><p className="text-sm font-medium text-[#2E3A46] mt-0.5">{animalPopup.mother_id}</p></div>)}
                {animalPopup.father_id && (<div className="p-3 rounded-xl bg-[#EEF3F8]"><p className="text-xs text-[#7A9BB5]">Vater</p><p className="text-sm font-medium text-[#2E3A46] mt-0.5">{animalPopup.father_id}</p></div>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FULLSCREEN IMAGE VIEWER */}
      {animalFullImg !== null && animalPopup?.photos?.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-[70] flex items-center justify-center" onClick={() => setAnimalFullImg(null)}>
          <button className="absolute top-4 right-4 text-white z-10 p-2 hover:bg-white/10 rounded-full" onClick={() => setAnimalFullImg(null)}><X size={28} /></button>
          <button className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white z-10 p-3 hover:bg-white/10 rounded-full bg-black/30"
            onClick={(e) => { e.stopPropagation(); setAnimalFullImg((animalFullImg - 1 + animalPopup.photos.length) % animalPopup.photos.length); }}>
            <ChevronLeft size={28} />
          </button>
          <img src={`${BACKEND_URL}${animalPopup.photos[animalFullImg]}`} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white z-10 p-3 hover:bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); setAnimalFullImg((animalFullImg + 1) % animalPopup.photos.length); }}>
            <ChevronRight size={28} />
          </button>
          <div className="absolute bottom-4 text-white text-sm">{animalFullImg + 1} / {animalPopup.photos.length}</div>
        </div>
      )}

      {/* USP POPUP */}
      {uspPopup && (
        <div className="fixed inset-0 bg-[#2E3A46]/80 z-[60] flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => setUspPopup(null)}>
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg p-5 md:p-8 shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <button className="float-right w-8 h-8 rounded-full bg-[#EEF3F8] flex items-center justify-center text-[#7A9BB5] hover:bg-[#D8E8F0]" onClick={() => setUspPopup(null)}><X size={16} /></button>
            <h3 className="font-heading text-xl text-[#2E3A46] mb-4 pr-8">{uspPopup.title}</h3>
            <p className="text-[#354551] leading-relaxed whitespace-pre-line">{uspPopup.body}</p>
          </div>
        </div>
      )}

      {/* REGISTER PROMPT MODAL */}
      {showRegisterPrompt && (
        <div className="fixed inset-0 bg-[#2E3A46]/80 z-[60] flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => setShowRegisterPrompt(false)}>
          <div className="bg-white rounded-t-2xl md:rounded-2xl p-5 md:p-8 w-full md:max-w-md shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#D8E8F0] flex items-center justify-center mx-auto mb-4"><BookOpen size={24} className="text-[#7A9BB5]" /></div>
              <h3 className="font-heading text-xl text-[#2E3A46] mb-2">Anfrage gesendet!</h3>
              <p className="text-sm text-[#354551]">Möchten Sie sich kostenlos registrieren? Als Interessent erhalten Sie Zugang zu:</p>
            </div>
            <div className="space-y-2 mb-6">
              {['Wissensportal mit 16 Kapiteln zur Birmahaltung', 'Aktuelle Würfe & Kitten-Informationen', 'Persönliche Empfehlungen & Produkte', 'Direkte Kommunikation mit Dorothea'].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-[#354551]"><ArrowRight size={14} className="text-[#7A9BB5] mt-0.5 shrink-0" />{item}</div>
              ))}
            </div>
            <div className="space-y-3 mb-4">
              <div className="relative"><label className="block text-sm font-medium text-[#2E3A46] mb-1">Passwort wählen</label><input type={showRegPw ? 'text' : 'password'} value={registerForm.password} onChange={(e) => setRegisterForm(f => ({ ...f, password: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none pr-10" placeholder="Mind. 6 Zeichen" /><button type="button" onClick={() => setShowRegPw(!showRegPw)} className="absolute right-3 top-8 text-[#C5D0DA]">{showRegPw ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
              <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Passwort wiederholen</label><input type={showRegPw ? 'text' : 'password'} value={registerForm.password2} onChange={(e) => setRegisterForm(f => ({ ...f, password2: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" placeholder="Passwort wiederholen" /></div>
            </div>
            <button onClick={handleRegister} disabled={registering} className="w-full btn-primary flex items-center justify-center gap-2 py-3">
              {registering ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><UserPlus size={16} />Kostenlos registrieren</>}
            </button>
            <button onClick={() => { setShowRegisterPrompt(false); setFormData({ name: '', phone: '', email: '', plz: '', message: '', inquiry_type: 'general', housing: '', birman_experience: false, wanted_timeframe: '', has_other_pets: false, preferred_color: '', preferred_gender: '', how_found: '', intro_text: '' }); setFormStep(1); }} className="w-full mt-3 text-center text-sm text-[#7A9BB5] hover:text-[#2E3A46] py-2">Nein danke, vielleicht später</button>
          </div>
        </div>
      )}

      {/* KITTEN ANFRAGE POPUP */}
      {kittenPopup && (
        <div className="fixed inset-0 bg-[#2E3A46]/80 z-[60] flex items-end md:items-start justify-center p-0 md:p-4 md:pt-16 overflow-y-auto" onClick={() => setKittenPopup(false)}>
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg shadow-2xl animate-fade-in max-h-[95vh] md:max-h-none overflow-y-auto md:mb-8" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#2E3A46] to-[#354551] p-5 md:p-6 rounded-t-2xl relative">
              <button onClick={() => setKittenPopup(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><X size={18} /></button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/20 flex items-center justify-center"><Cat size={24} className="text-[#C9A96E]" /></div>
                <div>
                  <h3 className="font-heading text-xl text-white">{cms.kitten_popup?.title || 'Kitten-Anfrage'}</h3>
                  <p className="text-[#C5D0DA] text-sm">Interesse an einem Schneeblumen-Kitten?</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-[#354551] leading-relaxed mb-4">
                {cms.kitten_popup?.body || 'Wir freuen uns über Ihr Interesse an unseren Birma-Kitten!'}
              </p>

              {kittenForm._step !== 2 ? (
                <div className="space-y-3">
                  <p className="text-xs text-[#7A9BB5] font-medium">Schritt 1 von 2 – Ihre Kontaktdaten</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Name *</label>
                      <input value={kittenForm.name} onChange={(e) => setKittenForm(f => ({...f, name: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="Ihr Name" /></div>
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">E-Mail *</label>
                      <input type="email" value={kittenForm.email} onChange={(e) => setKittenForm(f => ({...f, email: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="ihre@email.de" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Telefon *</label>
                      <input type="tel" value={kittenForm.phone} onChange={(e) => setKittenForm(f => ({...f, phone: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="+49..." /></div>
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">PLZ</label>
                      <input value={kittenForm.plz || ''} onChange={(e) => setKittenForm(f => ({...f, plz: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="PLZ" /></div>
                  </div>
                  <button onClick={() => {
                    if (!kittenForm.name || !kittenForm.email || !kittenForm.phone) { toast.error('Bitte Name, E-Mail und Telefon ausfüllen'); return; }
                    setKittenForm(f => ({...f, _step: 2}));
                  }} className="w-full btn-primary flex items-center justify-center gap-2 py-3">
                    Weiter <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-[#C9A96E] font-medium">Schritt 2 von 2 – Erzählen Sie uns mehr</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Wohnsituation</label>
                      <select value={kittenForm.housing || ''} onChange={(e) => setKittenForm(f => ({...f, housing: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none">
                        <option value="">Bitte wählen...</option><option value="wohnung">Wohnung</option><option value="haus_garten">Haus mit Garten</option><option value="haus_ohne">Haus ohne Garten</option><option value="gehege">Mit Außengehege</option>
                      </select></div>
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Farbwunsch</label>
                      <select value={kittenForm.preferred_color || ''} onChange={(e) => setKittenForm(f => ({...f, preferred_color: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none">
                        <option value="">Keine Präferenz</option><option value="chocolate">Chocolate Point</option><option value="lilac">Lilac Point</option><option value="seal">Seal Point</option><option value="blue">Blue Point</option><option value="chocolate_tabby">Chocolate Tabby</option><option value="lilac_tabby">Lilac Tabby</option><option value="seal_tabby">Seal Tabby</option><option value="blue_tabby">Blue Tabby</option>
                      </select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Geschlecht</label>
                      <select value={kittenForm.preferred_gender || ''} onChange={(e) => setKittenForm(f => ({...f, preferred_gender: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none">
                        <option value="">Egal</option><option value="weiblich">Weiblich</option><option value="männlich">Männlich</option>
                      </select></div>
                    <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Zeitraum</label>
                      <input value={kittenForm.wanted_timeframe || ''} onChange={(e) => setKittenForm(f => ({...f, wanted_timeframe: e.target.value}))}
                        className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="z.B. Frühjahr 2026" /></div>
                  </div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={kittenForm.birman_experience || false} onChange={(e) => setKittenForm(f => ({...f, birman_experience: e.target.checked}))} className="w-3.5 h-3.5 accent-[#7A9BB5]" /> Birma-Erfahrung</label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={kittenForm.has_other_pets || false} onChange={(e) => setKittenForm(f => ({...f, has_other_pets: e.target.checked}))} className="w-3.5 h-3.5 accent-[#7A9BB5]" /> Andere Haustiere</label>
                  </div>
                  <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Persönliches Anschreiben</label>
                    <textarea value={kittenForm.message || ''} onChange={(e) => setKittenForm(f => ({...f, message: e.target.value}))} rows={3}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#C5D0DA] text-sm focus:border-[#7A9BB5] focus:outline-none resize-none"
                      placeholder="Erzählen Sie uns von sich und warum Sie sich eine Birma wünschen..." /></div>
                  <div className="flex gap-2">
                    <button onClick={() => setKittenForm(f => ({...f, _step: undefined}))} className="px-4 py-3 rounded-xl border border-[#C5D0DA] text-sm text-[#7A9BB5]">Zurück</button>
                    <button onClick={async () => {
                      setKittenSubmitting(true);
                      try {
                        await createInquiry({
                          name: kittenForm.name, email: kittenForm.email, phone: kittenForm.phone,
                          plz: kittenForm.plz || '', message: kittenForm.message || '', inquiry_type: 'interest',
                          housing: kittenForm.housing || '', birman_experience: kittenForm.birman_experience || false,
                          wanted_timeframe: kittenForm.wanted_timeframe || '', has_other_pets: kittenForm.has_other_pets || false,
                          preferred_color: kittenForm.preferred_color || '', preferred_gender: kittenForm.preferred_gender || '',
                          how_found: '', intro_text: kittenForm.message || ''
                        });
                        toast.success('Vielen Dank! Ihre Kitten-Anfrage wurde gesendet.');
                        setKittenPopup(false);
                        setKittenForm({ name: '', email: '', phone: '', message: '' });
                        if (!user) setShowRegisterPrompt(true);
                      } catch (err) { toast.error('Fehler beim Senden'); }
                      finally { setKittenSubmitting(false); }
                    }} disabled={kittenSubmitting}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 py-3">
                      {kittenSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        : <><Send size={16} /> Kitten-Anfrage senden</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* REVIEW POPUP */}
      {reviewPopup && (
        <div className="fixed inset-0 bg-[#2E3A46]/80 z-[60] flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => setReviewPopup(null)}>
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg p-5 md:p-8 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button className="float-right w-8 h-8 rounded-full bg-[#EEF3F8] flex items-center justify-center text-[#7A9BB5] hover:bg-[#D8E8F0]" onClick={() => setReviewPopup(null)}><X size={16} /></button>
            <div className="flex gap-0.5 mb-3">{[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} size={20} className={s <= reviewPopup.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-[#C5D0DA]'} />
            ))}</div>
            <p className="text-[#354551] leading-relaxed mb-4 italic">"{reviewPopup.text}"</p>
            <p className="text-base font-heading text-[#2E3A46] mb-4">— {reviewPopup.name}</p>
            {reviewPopup.photos?.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mt-4">
                {reviewPopup.photos.map((url, i) => (
                  <img key={i} src={`${BACKEND_URL}${url}`} alt="Bewertungsfoto" className="rounded-xl w-full aspect-square object-cover" />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FOOTER */}
      <Footer settings={settings} />
    </div>
  );
};

export default LandingPage;
